import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, DownloadSimple, FilePdf, CheckCircle, MinusCircle, Sparkle, Signature, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

// Build labels & options
const CYCLES = [
  { v: "monthly",   l: "Monthly",   hint: "Operational finance control + monthly management review" },
  { v: "quarterly", l: "Quarterly", hint: "Management review, donor alignment, period performance" },
  { v: "yearly",    l: "Yearly",    hint: "Annual reporting, audit readiness, strategic review" },
];

// Defaults — previous closed period
function defaultPeriod(cycle) {
  const today = new Date();
  if (cycle === "yearly") return String(today.getFullYear() - 1);
  if (cycle === "quarterly") {
    const q = Math.floor(today.getMonth() / 3) + 1;
    if (q === 1) return `${today.getFullYear() - 1}-Q4`;
    return `${today.getFullYear()}-Q${q - 1}`;
  }
  // monthly
  const y = today.getFullYear();
  const m = today.getMonth(); // already previous (0-based)
  if (m === 0) return `${y - 1}-12`;
  return `${y}-${String(m).padStart(2, "0")}`;
}

function periodOptions(cycle) {
  const today = new Date();
  const y = today.getFullYear();
  if (cycle === "yearly") {
    return [y - 3, y - 2, y - 1, y, y + 1].map(yr => ({ v: String(yr), l: String(yr) }));
  }
  if (cycle === "quarterly") {
    const out = [];
    for (let yr = y - 2; yr <= y + 1; yr++) {
      for (let q = 1; q <= 4; q++) out.push({ v: `${yr}-Q${q}`, l: `Q${q} ${yr}` });
    }
    return out;
  }
  // monthly — last 24 months and next 6
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const out = [];
  for (let i = -24; i <= 6; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    out.push({ v: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`,
                l: `${months[d.getMonth()]} ${d.getFullYear()}` });
  }
  return out;
}

export default function FinancePacksTab() {
  const [cycle, setCycle] = useState("monthly");
  const [period, setPeriod] = useState(() => defaultPeriod("monthly"));
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const opts = useMemo(() => periodOptions(cycle), [cycle]);

  // Re-default period when cycle changes
  useEffect(() => { setPeriod(defaultPeriod(cycle)); }, [cycle]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/exports/finance-pack/preview", { params: { cycle, period } })
       .then(({ data }) => alive && setPreview(data))
       .catch(err => { if (alive) toast.error(formatApiError(err)); })
       .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [cycle, period]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/exports/finance-pack.pdf", {
        params: { cycle, period }, responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m ? m[1] : `descon_finance-pack_${cycle}_${period}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Generated · ${filename}`);
    } catch (err) {
      if (err?.response?.data instanceof Blob) {
        try { const t = await err.response.data.text(); toast.error(JSON.parse(t).detail || "Pack generation failed"); }
        catch { toast.error("Pack generation failed"); }
      } else toast.error(formatApiError(err));
    } finally { setDownloading(false); }
  };

  return (
    <div className="space-y-5" data-testid="finance-packs-tab">
      <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="font-display text-xl font-semibold text-slate-900 flex items-center gap-2">
              <Sparkle weight="fill" size={18} className="text-[#1E5CBA]" />
              Finance Packs
            </h2>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Generate a bundled, audit-ready PDF that combines all the standard period-close finance
              reports — for monthly review, quarterly donor alignment or yearly audit support.
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
          {CYCLES.map(c => (
            <button
              key={c.v}
              type="button"
              onClick={() => setCycle(c.v)}
              data-testid={`pack-cycle-${c.v}`}
              className={`rounded-lg border-2 p-4 text-left transition ${
                cycle === c.v
                  ? "border-[#1E5CBA] bg-[#1E5CBA]/5 shadow-sm"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <div className="font-display text-base font-semibold text-slate-900">{c.l} Finance Pack</div>
              <div className="text-xs text-slate-500 mt-1 leading-snug">{c.hint}</div>
              {cycle === c.v && (
                <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono-alt uppercase tracking-wider text-[#1E5CBA]">
                  <CheckCircle weight="fill" size={12} /> Selected
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">
                Reporting period
              </Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-56" data-testid="pack-period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {opts.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {preview && (
              <div className="text-xs text-slate-600">
                <div className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">Period dates</div>
                <div>{preview.start} → {preview.end}</div>
              </div>
            )}
          </div>
          <Button
            onClick={download}
            disabled={downloading || loading}
            data-testid="pack-download-btn"
            className="btn-primary-blue"
            size="lg"
          >
            <DownloadSimple weight="bold" size={16} className="mr-2" />
            {downloading ? "Generating PDF…" : "Generate Finance Pack PDF"}
          </Button>
        </div>
      </div>

      {/* Prepared-by / Reviewed-by sign-off */}
      <PackSignoffCard cycle={cycle} period={period} />

      {/* KPI strip */}
      {preview?.kpis && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <KpiCard label="Revenue" value={formatCurrency(preview.kpis.revenue)} tone="blue" />
          <KpiCard label="Expense" value={formatCurrency(preview.kpis.expense)} tone="amber" />
          <KpiCard label="Net position" value={formatCurrency(preview.kpis.net)} tone={preview.kpis.net >= 0 ? "emerald" : "red"} />
          <KpiCard label="Opening cash" value={formatCurrency(preview.kpis.opening_cash)} tone="slate" />
          <KpiCard label="Closing cash" value={formatCurrency(preview.kpis.closing_cash)} tone={preview.kpis.closing_cash >= 0 ? "emerald" : "red"} />
          <KpiCard label="Receivables" value={formatCurrency(preview.kpis.receivables)} tone="navy" />
        </div>
      )}

      {/* Sections preview */}
      <div className="dc-card overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="font-display text-[15px] font-semibold text-slate-900">
              Pack contents preview
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              These sections will appear in the generated PDF — in this order. Empty sections show
              "Nothing to report this period" so the audit trail stays consistent.
            </p>
          </div>
          {preview && (
            <span className="rounded-full bg-[#1E5CBA]/10 text-[#1E5CBA] px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-widest">
              {preview.label}
            </span>
          )}
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading preview…</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {(preview?.sections || []).map((s, i) => {
              const empty = s.count === 0;
              return (
                <li key={s.name} className="px-5 py-3 flex items-center gap-3 text-sm"
                    data-testid={`pack-section-${i + 1}`}>
                  <span className="w-7 text-right font-mono-alt text-xs text-slate-400 tabular-nums">{i + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold ${empty ? "text-slate-500" : "text-slate-900"}`}>
                      {s.name}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{s.summary}</div>
                  </div>
                  {s.count !== null && s.count !== undefined && (
                    <span className={`rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-wider ${
                      empty
                        ? "bg-slate-100 text-slate-500"
                        : "bg-emerald-100 text-emerald-800"
                    }`}>
                      {empty ? <span className="inline-flex items-center gap-1"><MinusCircle size={10} /> empty</span>
                              : `${s.count} record(s)`}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-600 flex items-start gap-2">
        <Calendar size={14} className="text-slate-400 shrink-0 mt-0.5" />
        <span>
          Cover page · Table of Contents with page numbers · 11 sections · Sign-off block.
          Pack defaults to the <b>previous closed period</b>; switch the period from the dropdown to
          regenerate for any month, quarter or year.
        </span>
      </div>
    </div>
  );
}

function KpiCard({ label, value, tone }) {
  const tones = {
    navy:    "border-l-[#0A2342]",
    blue:    "border-l-[#1E5CBA]",
    emerald: "border-l-emerald-500",
    amber:   "border-l-amber-500",
    red:     "border-l-red-500",
    slate:   "border-l-slate-400",
  };
  return (
    <div className={`dc-card p-3 border-l-4 ${tones[tone] || tones.navy}`}>
      <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 font-display text-lg font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

// ─────────────── Prepared-by / Reviewed-by sign-off (mandate §13) ───────────────
function PackSignoffCard({ cycle, period }) {
  const [record, setRecord] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/finance/packs/signoffs", { params: { cycle, period } })
      .then(({ data }) => setRecord((data.items || [])[0] || null))
      .catch(() => {});
  };
  useEffect(() => { load(); }, [cycle, period]); // eslint-disable-line react-hooks/exhaustive-deps

  const prepare = async () => {
    setBusy(true);
    try {
      await api.post("/finance/packs/signoff", { cycle, period });
      toast.success("Pack prepared — awaiting reviewer sign-off");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const review = async (verdict) => {
    setBusy(true);
    try {
      await api.post(`/finance/packs/signoff/${record.id}/review`, { verdict });
      toast.success(verdict === "approved" ? "Pack signed off" : "Pack rejected — re-prepare after fixes");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const tone = record?.status === "reviewed" ? "border-l-emerald-500"
    : record?.status === "rejected" ? "border-l-rose-500"
    : record?.status === "prepared" ? "border-l-amber-500" : "border-l-slate-300";

  return (
    <div className={`dc-card border-l-4 ${tone} p-4`} data-testid="pack-signoff-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Signature size={16} weight="fill" className="text-[#1E5CBA]" />
            <h3 className="font-display text-[15px] font-semibold text-slate-900">
              Sign-off · {cycle} {period}
            </h3>
          </div>
          {record ? (
            <div className="mt-1 text-xs text-slate-600" data-testid="pack-signoff-status">
              <b className="uppercase">{record.status}</b>
              {" · "}prepared by {record.prepared_by}
              {record.reviewed_by && <> · {record.status === "reviewed" ? "signed off" : "rejected"} by {record.reviewed_by}</>}
            </div>
          ) : (
            <div className="mt-1 text-xs text-slate-400" data-testid="pack-signoff-status">
              Not yet prepared — capture the prepared-by / reviewed-by evidence for this period.
            </div>
          )}
          <div className="mt-1 text-[10px] text-slate-400">
            Segregation of duties: the preparer cannot review their own pack.
          </div>
        </div>
        <div className="flex gap-2">
          {(!record || record.status === "rejected") && (
            <Button size="sm" className="btn-primary-blue h-8" onClick={prepare} disabled={busy} data-testid="pack-signoff-prepare">
              <Signature size={13} className="mr-1" /> Prepare pack
            </Button>
          )}
          {record?.status === "prepared" && (
            <>
              <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => review("approved")} disabled={busy} data-testid="pack-signoff-approve">
                <CheckCircle size={13} className="mr-1" /> Sign off
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-rose-600 border-rose-200" onClick={() => review("rejected")} disabled={busy} data-testid="pack-signoff-reject">
                <XCircle size={13} className="mr-1" /> Reject
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
